/**
 * Maison Marcel — accessibility patches
 *
 * Behaviour the theme is missing, kept here rather than in main.js so a theme
 * update cannot quietly swallow it. Everything below drives the theme's own
 * controls rather than reaching into component internals, so the overlay,
 * body classes and open/closed state stay consistent with whatever the theme
 * does when the same control is clicked.
 */
(() => {
  const isVisible = (el) => {
    if (!el) return false;
    const r = el.getBoundingClientRect();
    if (!r.width || !r.height) return false;
    const cs = getComputedStyle(el);
    return cs.visibility !== 'hidden' && cs.display !== 'none';
  };

  /*
   * Form errors.
   *
   * The contact forms rely on the browser's own validation. That does block
   * the submit and does move focus to the first empty field, which is the
   * hard part — but the only explanation of what is wrong is the transient
   * bubble the browser pops up, which vanishes on the next keystroke, is not
   * in the page, and leaves nothing a screen reader can go back to. Nothing
   * marked the field as invalid either.
   *
   * So the bubble is suppressed and the browser's own message is written into
   * the page instead, tied to its field, and left there until the field is
   * fixed. The wording comes from the browser, so it stays correct and
   * translated ("Please enter an email address", and so on).
   */
  const ERROR_CLASS = 'mm-field-error';

  const errorNodeFor = (field) => {
    const id = `${field.id || field.name.replace(/[^\w-]/g, '-')}-error`;
    let node = document.getElementById(id);
    if (!node) {
      node = document.createElement('p');
      node.id = id;
      node.className = ERROR_CLASS;
      // polite, not assertive: the field is already announced on focus, and an
      // assertive region would talk over it.
      node.setAttribute('role', 'status');
      field.insertAdjacentElement('afterend', node);
    }
    return node;
  };

  const markInvalid = (field) => {
    const node = errorNodeFor(field);
    node.textContent = field.validationMessage;
    field.setAttribute('aria-invalid', 'true');
    const describedBy = (field.getAttribute('aria-describedby') || '').split(/\s+/).filter(Boolean);
    if (!describedBy.includes(node.id)) {
      describedBy.push(node.id);
      field.setAttribute('aria-describedby', describedBy.join(' '));
    }
  };

  const clearInvalid = (field) => {
    field.removeAttribute('aria-invalid');
    const id = `${field.id || field.name.replace(/[^\w-]/g, '-')}-error`;
    const node = document.getElementById(id);
    if (node) node.textContent = '';
  };

  const isContactForm = (el) => !!(el && el.closest('form[action*="/contact"]'));

  // 'invalid' does not bubble, so it has to be caught on the way down.
  //
  // Suppressing the bubble also drops the browser's own "jump to the first bad
  // field" behaviour, so that has to be done by hand. The browser fires these
  // in document order and a blocked submit never raises a 'submit' event at
  // all, so the first one through this handler in a given pass is the field to
  // land on; the flag clears once the pass is over.
  let focusedThisPass = false;
  document.addEventListener(
    'invalid',
    (evt) => {
      const field = evt.target;
      if (!isContactForm(field)) return;
      evt.preventDefault(); // drop the browser bubble; the page carries it now
      markInvalid(field);

      if (!focusedThisPass) {
        focusedThisPass = true;
        field.focus();
        setTimeout(() => {
          focusedThisPass = false;
        }, 0);
      }
    },
    true
  );

  document.addEventListener('input', (evt) => {
    const field = evt.target;
    if (!isContactForm(field) || !field.checkValidity) return;
    if (field.checkValidity()) clearInvalid(field);
  }, true);

  /*
   * A submission that came back with errors from Shopify renders a summary but
   * never moves to it, so a screen reader user is returned to the top of the
   * page with no idea the attempt failed.
   */
  document.addEventListener('DOMContentLoaded', () => {
    const summary = document.querySelector('.marcel-inquiry__errors, .form-status--error, [class*="form-status"][class*="error"]');
    if (!summary || !summary.textContent.trim()) return;
    summary.setAttribute('tabindex', '-1');
    summary.setAttribute('role', 'alert');
    summary.focus();
  });

  const FOCUSABLE =
    'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),' +
    'textarea:not([disabled]),summary,[tabindex]:not([tabindex="-1"])';

  const focusablesIn = (root) =>
    [...root.querySelectorAll(FOCUSABLE)].filter((el) => {
      const r = el.getBoundingClientRect();
      return r.width > 0 && r.height > 0;
    });

  /*
   * Focus containment for the search panel.
   *
   * The panel dims the page behind it, so it reads as a modal and is marked as
   * one — but the theme never held focus inside it, and Tab walked straight out
   * into the page underneath while the overlay still covered it. Saying
   * aria-modal="true" without this would be a lie to a screen reader.
   */
  document.addEventListener(
    'keydown',
    (evt) => {
      if (evt.key !== 'Tab') return;
      const search = document.querySelector('main-header .header__search');
      if (!search || search.hidden) return;

      const items = focusablesIn(search);
      if (!items.length) return;

      const first = items[0];
      const last = items[items.length - 1];
      const active = document.activeElement;

      if (!search.contains(active)) {
        evt.preventDefault();
        (evt.shiftKey ? last : first).focus();
        return;
      }
      if (evt.shiftKey && active === first) {
        evt.preventDefault();
        last.focus();
      } else if (!evt.shiftKey && active === last) {
        evt.preventDefault();
        first.focus();
      }
    },
    true
  );

  /*
   * The menu drawer is only a dialog below the large breakpoint — above it the
   * same element is the nav sitting in the bar, which must not claim to be one.
   * So the role is put on and taken off as the drawer opens and closes rather
   * than written into the markup.
   */
  const syncMenuDialogRole = () => {
    const header = document.querySelector('main-header');
    if (!header) return;
    const panel = header.querySelector('.main-menu__content');
    const toggle = header.querySelector('.main-menu__toggle');
    if (!panel || !toggle) return;

    const toggleBox = toggle.getBoundingClientRect();
    const isDrawer = toggleBox.width > 0 && toggleBox.height > 0;
    const isOpen = !!header.querySelector('.main-menu__disclosure.is-open');

    if (isDrawer && isOpen) {
      panel.setAttribute('role', 'dialog');
      panel.setAttribute('aria-modal', 'true');
      if (!panel.getAttribute('aria-label')) panel.setAttribute('aria-label', 'Menu');
    } else {
      panel.removeAttribute('role');
      panel.removeAttribute('aria-modal');
    }
  };

  document.addEventListener('click', () => setTimeout(syncMenuDialogRole, 60), true);
  document.addEventListener('keyup', () => setTimeout(syncMenuDialogRole, 60), true);
  window.addEventListener('resize', syncMenuDialogRole);
  document.addEventListener('DOMContentLoaded', syncMenuDialogRole);

  document.addEventListener(
    'keydown',
    (evt) => {
      if (evt.key !== 'Escape') return;

      const header = document.querySelector('main-header');
      if (!header) return;

      /*
       * Search.
       *
       * The theme opens the search panel and registers a click handler on the
       * page overlay to close it again. With a mouse that is enough; from the
       * keyboard there is no way out except hunting for the close button,
       * because nothing listens for Escape. Clicking the theme's own toggle is
       * what a mouse user's overlay click ends up doing, so the panel, the
       * overlay and the focus all unwind the same way.
       */
      const search = header.querySelector('.header__search');
      if (search && !search.hidden) {
        const toggle = header.querySelector('a.js-toggle-search, .js-toggle-search');
        if (toggle) {
          evt.preventDefault();
          toggle.click();
          toggle.focus();
          return;
        }
      }

      /*
       * Nav dropdown.
       *
       * MainMenu registers its Escape handler on `this.nav` only while the
       * page overlay is showing, and a tier-1 dropdown on desktop opens
       * without raising that overlay — so nothing is listening and the panel
       * stays down. Clicking the summary is the theme's own close path.
       */
      const openDetails = header.querySelector('.main-nav details.is-open');
      if (openDetails) {
        const summary = openDetails.previousElementSibling?.matches('summary')
          ? openDetails.previousElementSibling
          : openDetails.querySelector('summary');
        if (summary) {
          evt.preventDefault();
          summary.click();
          summary.focus();
          return;
        }
      }

      /*
       * Mobile menu drawer.
       *
       * MainMenu does bind Escape, but its handler looks for `details.is-open`
       * *inside* this.nav — and on small screens the drawer that needs closing
       * is `.main-menu__disclosure`, an ancestor of that nav, so the query
       * never matches it and the drawer stays open. A dropdown open inside the
       * drawer is dealt with above, so by here the drawer itself is what the
       * Escape is asking to close.
       */
      const drawer = header.querySelector('.main-menu__disclosure.is-open');
      const toggle = header.querySelector('.main-menu__toggle');
      // The hamburger is hidden from lg up, so its visibility is also the test
      // for "the drawer is the thing on screen" rather than the desktop nav.
      if (drawer && toggle && isVisible(toggle)) {
        evt.preventDefault();
        toggle.click();
        toggle.focus();
      }
    },
    // Capture, so the panel closes even when focus sits on a control that
    // stops the event on its way back up.
    true
  );
})();
